export interface AffiliatePlan {
  percentage: string
  plan: string
  price: number
  discountedPrice: number
  promoDiscountedPrice: number
  commission: number
  description: string
}

export const affiliatePlans: AffiliatePlan[] = [
  {
    percentage: "100%",
    plan: "Monthly plan",
    price: 4.99,
    discountedPrice: 4.99,
    promoDiscountedPrice: 4.49,
    commission: 4.49,
    description: "(100% of what your referral pays)",
  },
  {
    percentage: "25%",
    plan: "Annual plan",
    price: 59.99,
    discountedPrice: 53.99,
    promoDiscountedPrice: 48.59,
    commission: 12.15,
    description: "(25% of the discounted price)",
  },
  {
    percentage: "25%",
    plan: "Two-year plan",
    price: 119.99,
    discountedPrice: 95.99,
    promoDiscountedPrice: 86.39,
    commission: 21.6,
    description: "(25% of the discounted price)",
  },
]

export function formatPrice(price: number, showDecimals = true): string {
  return Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(price)
}

export const howItWorksSteps = [
  {
    iconSrc: "/marketing/go/icons/community.svg",
    title: "1. Sign Up",
    description:
      "Fill out our form to join our affiliate program. Once approved, you'll receive your unique affiliate code.",
  },
  {
    iconSrc: "/marketing/go/icons/collaboration.svg",
    title: "2. Promote Your Code",
    description:
      "Share your affiliate code with your audience via your website, social media, or any platform. Customers can use your code at checkout or via a referral link (e.g., https://totodo.app?affiliate=YOUR_CODE).",
  },
  {
    iconSrc: "/marketing/go/icons/star-pink.svg",
    title: "3. Earn Commissions",
    description:
      "When a customer uses your code and makes a purchase, you earn commissions based on their plan. Plus, they get a 10% discount!",
  },
]

export const whyJoinBenefits = [
  {
    iconSrc: "/marketing/go/icons/star-pink.svg",
    title: "No Limits",
    description:
      "There's no cap on how many referrals you can make. The more you promote, the more you earn!",
  },
  {
    iconSrc: "/marketing/go/icons/rocket-pink.svg",
    title: "Easy to start",
    description:
      "Just fill in the form below and we'll set up your unique code. That's it!",
  },
  {
    iconSrc: "/marketing/go/icons/community.svg",
    title: "Support and resources",
    description:
      "We're here to help you succeed with marketing tips and answers to your questions.",
  },
]

export interface FaqItem {
  id: string
  question: string
  answer: string
}

export const affiliateFaqData: FaqItem[] = [
  {
    id: "faq-1",
    question: "How does the To To-Do affiliate program work?",
    answer: `It's simple! Once approved, you'll receive a unique affiliate link and/or code. Share this link/code on your website, blog, social media, etc. When someone clicks your link and makes a qualified purchase of a To To-Do plan within the cookie duration period, you earn a commission.`,
  },
  {
    id: "faq-2",
    question: "How much can I earn?",
    answer: `We offer a competitive commission structure. You earn 100% of the first month's payment for monthly plans, and 25% of the payment for annual and two-year plans (calculated on the discounted price if the customer uses your code). Please see the "Commission Structure" section on our main <a href="/affiliate">affiliate page</a> for detailed examples and current pricing.`,
  },
  {
    id: "faq-pricing",
    question: "How does the pricing and discount structure work for referred customers?",
    answer: `Customers using your code get a 10% discount on their purchase.<br/><br/>Monthly plan: Original 4,99 €, discounted to 4,49 €, you earn 4,99 €.<br/>Annual plan: Original 59,99 €, discounted to 53,99 €, you earn 25% of 53,99 € = 13,50 €.<br/>Two-year plan: Original 99,99 €, discounted to 89,99 €, you earn 25% of 89,99 € = 22,50 €.`,
  },
  {
    id: "faq-3",
    question: "How long does the tracking cookie last?",
    answer: `Our tracking cookie lasts for <strong>60 days</strong>. This means if a user clicks your link and purchases a plan within 60 days (without clicking another affiliate's link in the meantime), you'll be credited for the referral.`,
  },
  {
    id: "faq-4",
    question: "How and when do I get paid?",
    answer: `Payments are made monthly, typically around the 15th, for commissions earned in the previous month. We require a minimum balance of <strong>$50 USD</strong> (or equivalent) before processing a payout. Payments are currently made via <strong>PayPal</strong>. Ensure your payment details are correctly filled out in your affiliate account/dashboard.`,
  },
  {
    id: "faq-5",
    question: "How can I track my referrals and earnings?",
    answer: `You will have access to an affiliate dashboard where you can monitor your clicks, referrals, qualified purchases, and commission earnings in real-time. We'll provide login details once your application is approved.`,
  },
  {
    id: "faq-6",
    question: "Are there any restrictions on how I can promote To To-Do?",
    answer: `Yes. Please refer to our <a href="/affiliate/terms">Affiliate Program Terms of Service</a> for full details. Key restrictions include: no spamming, no bidding on our branded keywords (like "To To-Do") in PPC campaigns without permission, no using misleading claims, and adhering to all applicable laws and advertising guidelines (like FTC disclosure rules).`,
  },
  {
    id: "faq-7",
    question: "Does it cost anything to join?",
    answer: `No, joining the To To-Do affiliate program is completely free! You can start earning commissions without any upfront costs. However, you must comply with our <a href="/affiliate/terms">Affiliate Program Terms of Service</a> to maintain your account in good standing.`,
  },
  {
    id: "faq-8",
    question: "What happens if a customer I referred gets a refund?",
    answer: `If a customer receives a refund for a purchase you were credited for (e.g., within a money-back guarantee period or via a chargeback), the corresponding commission for that transaction will be deducted from your future payout.`,
  },
]
